"use client";

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

interface SpotlightScrollActivatorProps {
  children: ReactNode;
  className?: string;
}

/**
 * Mobile "hover on scroll" (Figma 12364:98123): below `lg` there is no hover,
 * so the card crossing the middle of the viewport gets `data-active="true"`
 * and plays the same reveal as desktop hover. Cards opt in with
 * `[data-spotlight-card]`; their styles key off `group-data-[active=true]`.
 */
export function SpotlightScrollActivator({
  children,
  className,
}: SpotlightScrollActivatorProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const mobile = window.matchMedia("(max-width: 1055.98px)");
    const cards = [
      ...container.querySelectorAll<HTMLElement>("[data-spotlight-card]"),
    ];
    const clear = () => {
      for (const card of cards) card.removeAttribute("data-active");
    };

    let observer: IntersectionObserver | undefined;
    const connect = () => {
      observer?.disconnect();
      clear();
      if (!mobile.matches) return;

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const card = entry.target as HTMLElement;
            if (entry.isIntersecting) {
              card.setAttribute("data-active", "true");
            } else {
              card.removeAttribute("data-active");
            }
          }
        },
        // Thin band around the vertical centre of the viewport.
        { rootMargin: "-45% 0px -45% 0px" }
      );
      for (const card of cards) observer.observe(card);
    };

    connect();
    mobile.addEventListener("change", connect);
    return () => {
      mobile.removeEventListener("change", connect);
      observer?.disconnect();
      clear();
    };
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
